import { inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { format } from 'date-fns';
import { ConfigBaseFilter, ConfigService, ListLayout, ListSortOption, SkeletonLoaderLayout } from 'gung-list';
import { BehaviorSubject, Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import {
  PunchoutAuditDateRange,
  PunchoutAuditEventType,
  PunchoutAuditListItem,
  PunchoutAuditLogEntry,
  PunchoutCustomerConfig
} from '../models/punchout';
import { PunchoutAuditAdminService } from './punchout/punchout-audit-admin.service';
import { PunchoutConfigAdminService } from './punchout/punchout-config-admin.service';

/**
 * Shared plumbing for the punchout audit list views.
 *
 * Two conditions give this class its shape. First, the list endpoint has no paging and gives each
 * entry with all fields. Thus this class removes the large fields at the arrival of the response,
 * and it always sends a date range. The detail view gets a single entry by its id. Second, the
 * audit document holds no configuration name. Thus this class also gets the configuration list and
 * makes a label from the two results.
 */
export abstract class PunchoutAuditListConfigBase implements ConfigService<PunchoutAuditListItem> {
  protected auditService = inject(PunchoutAuditAdminService);
  protected configAdminService = inject(PunchoutConfigAdminService);

  skeletonLoaderLayout: SkeletonLoaderLayout = 'table';

  /** Which event type this list shows. */
  protected abstract readonly eventType: PunchoutAuditEventType;

  // Drives re-fetching. filter-list subscribes to getItems() for the component's lifetime, so
  // emitting here refreshes the list in place. Errors are caught inside the switchMap, to keep
  // this outer stream alive for the next attempt.
  private readonly refreshTrigger = new BehaviorSubject<void>(undefined);

  // The initial range is the last month.
  private range: PunchoutAuditDateRange = defaultDateRange();

  getDateRange(): PunchoutAuditDateRange {
    return { ...this.range };
  }

  /**
   * Makes the range the last month again. The tab calls this method at each visit. This service has
   * a root scope and holds its state for the full session. Thus a wider range from an earlier visit
   * remains without this method.
   *
   * The method starts no new request. The list makes its first request at the mount of the tab.
   */
  resetDateRange(): void {
    this.range = defaultDateRange();
  }

  /**
   * Sets the date range and gets the entries again. The class does nothing if a limit is absent or
   * if the start date is after the end date. The list then keeps the last result.
   */
  setDateRange(range: PunchoutAuditDateRange): void {
    if (!range.from || !range.to || range.from > range.to) {
      return;
    }
    this.range = { from: range.from, to: range.to };
    this.refresh();
  }

  refresh(): void {
    this.refreshTrigger.next();
  }

  getItems(route?: ActivatedRouteSnapshot): Observable<PunchoutAuditListItem[]> {
    return this.refreshTrigger.pipe(
      switchMap(() =>
        forkJoin({
          entries: this.auditService.list(this.eventType, undefined, undefined, this.range),
          configs: this.configAdminService.list()
        }).pipe(
          catchError(() => of({ entries: [] as PunchoutAuditLogEntry[], configs: [] as PunchoutCustomerConfig[] }))
        )
      ),
      map(({ entries, configs }) => {
        const configsById = new Map((configs ?? []).map(config => [config.id, config]));
        return (entries ?? []).map(entry => this.toListItem(entry, configsById));
      })
    );
  }

  getItemId(item: PunchoutAuditListItem): string {
    return item.id;
  }

  getSearchTerms(item: PunchoutAuditListItem): string[] {
    return [
      item.configLabel,
      item.customerConfigQualifier,
      item.customerConfigId,
      item.username,
      item.payloadId,
      item.ediMessageId,
      item.errorMessage,
      item.status,
      item.target
    ].filter((term): term is string => !!term);
  }

  getSortOptions(route?: ActivatedRouteSnapshot): ListSortOption<PunchoutAuditListItem>[] {
    // The backend already sorts entries newest first. filter-list keeps that order as the default.
    return [];
  }

  getBatchSizes(route?: ActivatedRouteSnapshot): number[] {
    return [25, 50, 100];
  }

  abstract getFilters(route?: ActivatedRouteSnapshot): ConfigBaseFilter<PunchoutAuditListItem>[];

  abstract getLayouts(route?: ActivatedRouteSnapshot): ListLayout<PunchoutAuditListItem>[];

  /**
   * Removes the large fields and makes the row. The row count and the payload kind come from the
   * stored metadata. The browser that made the transfer calculated these values.
   */
  protected toListItem(
    entry: PunchoutAuditLogEntry,
    configsById: Map<string, PunchoutCustomerConfig>
  ): PunchoutAuditListItem {
    const { rawDocument, original, converted, ...rest } = entry;
    const config = entry.customerConfigId ? configsById.get(entry.customerConfigId) : undefined;
    return {
      ...rest,
      configLabel: config?.name || entry.customerConfigQualifier || entry.customerConfigId || '-',
      configMissing: !!entry.customerConfigId && !config,
      rowCount: entry.metadata?.rowCount,
      payloadKind: entry.metadata?.payloadKind,
      // The outcome applies to a cart transfer only. An order request has no converted payload, and
      // its `status` field holds the result. A calculation for an order request gives FAILED on
      // each row.
      outcome: this.eventType === 'PUNCHOUT_ORDER_MESSAGE' ? (converted ? 'OK' : 'FAILED') : undefined
    };
  }
}

/** The last month, from the same day of the last month to today. */
function defaultDateRange(): PunchoutAuditDateRange {
  const to = new Date();
  const from = new Date(to);
  from.setMonth(from.getMonth() - 1);
  return { from: format(from, 'yyyy-MM-dd'), to: format(to, 'yyyy-MM-dd') };
}
